import { Injectable } from '@angular/core';
import { AuthService } from '../../auth';
import { LoggerService } from '../../logger';
import { StateService, collator } from '../../utils';
import {
  BehaviorSubject,
  interval,
  merge,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  filter,
  map,
  startWith,
  switchMap,
  withLatestFrom,
  take,
} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import {
  ChatMessage,
  ListUserChatRoomsVm,
  ListChatEmojisVm,
  ListUserChatMessagesVm,
  EmojiDto,
  ChatRoom,
  ChatSignalRRequests,
  MessageRequest,
  PersonalChatRequest,
  PersonalChatOpenMessage,
  PersonalChatCloseRequest,
  PersonalChatCloseMessage,
  GetChatroomUsersRequest,
  ChatroomUsersMessage,
  AddToGroupsRequest,
} from '../models';
import { EnvironmentVariables } from '../../environment-variables';
import { LookupService, UserNamePipe } from '../../shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from '../components/confirmation-modal.component';
import {
  ListChatSupervisorsChildDto,
  ListChatSupervisorsVm,
} from '../models/list-chat-supervisor-vm';
import { ChatUserListComponent } from '../components/chat-user-list.component';
import { ChatroomUsers } from '../models/chatroom-users';

@Injectable({
  providedIn: 'root',
})
export class ChatService extends StateService<State> {
  private chatApi = `${this.environment.apiPath}/chat`;
  private connected$ = this.select((state) => state.hubState).pipe(
    filter((state) => state === HubConnectionState.Connected)
  );
  private hubConn: HubConnection;
  private prePersonalChatroom: string | undefined = undefined;
  private privateChatAdmins: number[] = [];
  private refreshImmediately = new Subject<void>();
  private refreshSub?: Subscription;
  private signalrRetries = 0;

  activeRoom$ = this.select((state) => state.activeRoom);
  allchat$ = this.select((state) =>
    state.rooms?.filter((room) => room.allchat === true)
  );
  allchatroom$ = this.select((state) => state.allChatSelectedRoom);
  chatSupervisors$ = this.select((state) => state.chatSupervisors);
  chatroomUsers$ = this.select((state) =>
    state.chatroomUsers.get(
      state.activeRoom! === 'All Chat'
        ? state.allChatSelectedRoom
        : state.activeRoom!
    )
  );
  emojis$ = this.select((state) => state.emojis);
  favourites$ = this.select((state) =>
    state.rooms?.filter((room) => room.favourite === true)
  );
  messages$ = this.select((state) => state.messages);
  readOnly$ = this.select((state) => state.readOnly);
  rooms$ = this.select((state) => state.rooms);
  showUserList$ = this.select((state) => state.showUserList);

  constructor(
    private logger: LoggerService,
    private http: HttpClient,
    private authSvc: AuthService,
    private environment: EnvironmentVariables,
    private lookupSvc: LookupService,
    private modalSvc: NgbModal
  ) {
    super(initalState);
    this.getEmojis$().subscribe((emojis) => (this.state = { emojis }));
    this.getChatSupervisors();

    this.hubConn = this.signalrInit();
  }

  private addToGroups(groups: string[]) {
    this.connected$.pipe(take(1)).subscribe(() => {
      this.logger.info('ChatService', `addToGroups`, groups)();
      this.signalrSend(new AddToGroupsRequest({ groups }));
    });
  }

  private getEmojis$() {
    return this.http
      .get<ListChatEmojisVm>(`${this.chatApi}/emojis`)
      .pipe(map((vm) => vm.emojis));
  }

  private getMessages$(rooms: string[]) {
    if (rooms.length <= 10)
      return this.http
        .get<ListUserChatMessagesVm>(`${this.chatApi}/messages`, {
          params: { rooms },
        })
        .pipe(map((vm) => vm.messages));

    return this.http
      .post<ListUserChatMessagesVm>(`${this.chatApi}/messages`, rooms)
      .pipe(map((vm) => vm.messages));
  }

  private getPersonalMessages$(userId: number) {
    return this.http
      .post<ListUserChatMessagesVm>(`${this.chatApi}/personalmessages`, userId)
      .pipe(map((vm) => vm.messages));
  }

  private getRooms$() {
    return this.http
      .get<ListUserChatRoomsVm>(`${this.chatApi}/rooms`)
      .pipe(map((vm) => vm.rooms));
  }

  private onChatroomUsers(msg: ChatroomUsersMessage) {
    this.logger.debug('ChatService', 'onChatroomUsers', msg)();
    this.state = {
      chatroomUsers: this.state.chatroomUsers.set(msg.group, msg.users),
    };
  }

  private onMessage(msg: ChatMessage) {
    this.logger.info('ChatService', 'onMessage', msg)();
    this.state = {
      messages: [...(this.state.messages || []), this.parseMessage(msg)],
    };
  }

  private onPersonalChat(msg: PersonalChatOpenMessage) {
    console.log('received personal chat message');
    this.authSvc.userId$.subscribe((myuserid) => {
      if (myuserid === msg.userId) {
        this.addMyPersonalChatroom(msg.userId, msg.requestingUserId);

        if (!this.privateChatAdmins.includes(msg.requestingUserId))
          this.privateChatAdmins.push(msg.requestingUserId);
      }
    });
  }

  private onPersonalChatClose(msg: PersonalChatCloseMessage) {
    console.log('received personal chat close message');
    this.authSvc.userId$.subscribe((myuserid) => {
      if (myuserid === msg.userId) {
        this.privateChatAdmins = this.privateChatAdmins.filter(
          (userid) => userid != msg.requestingUserId
        );
        if (this.privateChatAdmins.length === 0)
          this.closeMyPersonalChat('U' + msg.userId.toString());
      }
    });
  }

  private onReloadMessages() {
    this.getMessages$(this.state.rooms?.map((r) => r.group) || []).subscribe(
      (messages) =>
        (this.state = {
          messages: messages.map((msg) => this.parseMessage(msg)),
        })
    );
  }

  private parseMessage(msg: ChatMessage) {
    if (msg.userId === 0) {
      try {
        const data = JSON.parse(msg.message);
        if (data && typeof data === 'object') {
          msg.data = data;
        }
      } catch (e) {}
    }
    return msg;
  }

  private signalrInit() {
    const retryTimes = [
      3000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
    ];
    const hubConn = new HubConnectionBuilder()
      .withUrl(this.environment.chatHub, {
        accessTokenFactory: () => this.authSvc.accessToken || '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) => {
          this.logger.warn(
            'ChatService',
            'hubConn.withAutomaticReconnect',
            context
          )();
          if (context.previousRetryCount < retryTimes.length) {
            return retryTimes[context.previousRetryCount];
          }
          return null;
        },
      })
      .configureLogging(LogLevel.Warning)
      .build();
    this.hubConn = hubConn;
    hubConn.onclose((error: Error | undefined) => {
      this.state = { hubState: hubConn.state };
      this.logger.error('ChatService', 'hubConn.onclose', error)();
      this.signalrRestart();
    });

    hubConn.onreconnecting(() => (this.state = { hubState: hubConn.state }));
    hubConn.onreconnected(() => (this.state = { hubState: hubConn.state }));
    this.authSvc.isLoggedIn$
      .pipe(
        filter((isLoggedIn) => !!isLoggedIn),
        take(1)
      )
      .subscribe(() => this.signalrStart());
    return hubConn;
  }

  private signalrRestart(): void {
    this.signalrRetries++;
    if (this.signalrRetries < 20) {
      // for the initial retries - schedule for every 30 seconds
      setTimeout(() => this.signalrStart(), 30000);
    } else {
      // then wait a further 10 minutes before trying the process again.
      this.signalrRetries = 0;
      setTimeout(() => this.signalrStart(), 600000);
    }
  }

  private signalrSend(request: ChatSignalRRequests) {
    this.connected$.pipe(take(1)).subscribe(() => {
      this.logger.info('ChatService', `SEND ${request.name}`, request)();
      if (request.payload === undefined) {
        this.hubConn.send(request.name);
      } else {
        this.hubConn.send(request.name, request.payload);
      }
    });
  }

  private signalrStart(): void {
    this.hubConn
      .start()
      .then(() => {
        this.signalrRetries = 0;
        this.logger.debug('ChatService', 'hubConn.start', this.hubConn.state)();
        this.hubConn.off('Message');
        this.hubConn.on('Message', (event) => this.onMessage(event));
        this.hubConn.off('ReloadMessages');
        this.hubConn.on('ReloadMessages', () => this.onReloadMessages());
        this.hubConn.off('PersonalChatOpen');
        this.hubConn.on('PersonalChatOpen', (event) =>
          this.onPersonalChat(event)
        );
        this.hubConn.off('PersonalChatClose');
        this.hubConn.on('PersonalChatClose', (event) =>
          this.onPersonalChatClose(event)
        );
        this.hubConn.off('ChatroomUsers');
        this.hubConn.on('ChatroomUsers', (event) =>
          this.onChatroomUsers(event)
        );
      })
      .catch((err) => {
        this.logger.error('ChatService', 'hubConn.start', err)();
        this.signalrRestart();
      })
      .finally(() => {
        this.state = { hubState: this.hubConn.state };
      });
  }

  addMyPersonalChatroom(userid: number, requestinguserid: number) {
    const existingRooms = this.state.rooms;

    const userNamePipe: UserNamePipe = new UserNamePipe(this.lookupSvc);

    var room: ChatRoom = {
      group: 'U' + userid.toString(),
      groupName: 'Personal',
      allchat: false,
      campaignIds: [],
      expertIds: [],
      favourite: true,
      readOnly: false,
      teamLeaderIds: [],
      private: true,
    };

    this.prePersonalChatroom = this.state.activeRoom;

    if (!existingRooms?.map((rm) => rm.group).includes(room.group)) {
      // place on the front of the array so that the favourite limit of 30
      // will not limit this room from being displayed.
      existingRooms?.unshift(room);

      this.getPersonalMessages$(userid)
        .pipe(withLatestFrom(userNamePipe.transform(requestinguserid, 'chat')))
        .subscribe(([personalmessages, fromusername]) => {
          var msgid = 0;

          if (personalmessages && personalmessages.length > 0) {
            const lastMsg: ChatMessage = personalmessages.slice(-1)[0];
            msgid = lastMsg.messageId + 1;
          }

          var chatRequestedMsg: ChatMessage = {
            data: undefined,
            group: 'U' + userid.toString(),
            message: fromusername + ' has started a chat with you',
            messageId: msgid,
            pinned: false,
            sent: new Date(),
            today: true,
            userId: userid,
          };

          personalmessages.push(chatRequestedMsg);

          this.state = {
            rooms: existingRooms,
            activeRoom: 'U' + userid.toString(),
            messages: this.state.messages?.concat(personalmessages),
            readOnly: false,
          };
        });
    } else {
      // already been opened so just make it not read only and shift focus.
      var currentPrivateRoom = existingRooms.find(
        (r) => r.groupName === room.groupName
      );

      var msgid = 0;

      if (this.state.messages) {
        const lastMsg: ChatMessage = this.state.messages?.slice(-1)[0];
        msgid = lastMsg.messageId + 1;
      }

      userNamePipe
        .transform(requestinguserid, 'chat')
        .subscribe((fromusername) => {
          var chatRequestedMsg: ChatMessage = {
            data: undefined,
            group: 'U' + userid.toString(),
            message: fromusername + ' has started a chat with you',
            messageId: msgid,
            pinned: false,
            sent: new Date(),
            today: true,
            userId: userid,
          };

          if (currentPrivateRoom) currentPrivateRoom.readOnly = false;

          this.state = {
            activeRoom: room.group,
            readOnly: false,
            messages: [...(this.state.messages || []), chatRequestedMsg],
          };
        });
    }
  }

  addPrivateChatroom(userid: number, requestinguserid: number) {
    const existingRooms = this.state.rooms;

    const userNamePipe: UserNamePipe = new UserNamePipe(this.lookupSvc);

    this.authSvc.userId$
      .pipe(
        switchMap((uid) => userNamePipe.transform(uid, 'name')),
        withLatestFrom(userNamePipe.transform(userid, 'chat'))
      )
      .subscribe(([myUsername, uname]) => {
        var room: ChatRoom = {
          group: 'U' + userid.toString(),
          groupName: uname,
          allchat: false,
          campaignIds: [],
          expertIds: [],
          favourite: true,
          readOnly: false,
          teamLeaderIds: [],
          private: true,
        };

        if (!existingRooms?.map((rm) => rm.group).includes(room.group)) {
          this.prePersonalChatroom = this.state.activeRoom;

          this.signalrSend(
            new PersonalChatRequest({
              userId: userid,
              requestingUserId: requestinguserid,
            })
          );

          existingRooms?.unshift(room);

          this.getPersonalMessages$(userid).subscribe((personalMessages) => {
            var msgid = 0;
            if (personalMessages && personalMessages.length > 0) {
              const lastMsg: ChatMessage = personalMessages.slice(-1)[0];
              msgid = lastMsg.messageId + 1;
            }

            var chatRequestedMsg: ChatMessage = {
              data: undefined,
              group: 'U' + userid.toString(),
              message: 'You started a private chat with ' + uname,
              messageId: msgid,
              pinned: false,
              sent: new Date(),
              today: true,
              userId: userid,
            };

            personalMessages.push(chatRequestedMsg);

            this.state = {
              rooms: existingRooms,
              activeRoom: 'U' + userid.toString(),
              messages: this.state.messages?.concat(personalMessages),
            };

            /*setTimeout(
              () =>
                this.send(
                  myUsername + ' has started a chat with you.',
                  room.group
                ),
              1000
            );*/
          });
        }
      });
  }

  chatUserName$(userid: number) {
    const userNamePipe: UserNamePipe = new UserNamePipe(this.lookupSvc);
    return userNamePipe.transform(userid, 'chat');
  }

  clearAllChat() {
    this.http
      .put(`${this.chatApi}/clearallchat`, {})
      .subscribe(() => this.refreshRooms());
  }

  clearFavourites() {
    this.http
      .put(`${this.chatApi}/clearfavourites`, {})
      .subscribe(() => this.refreshRooms());
  }

  closeMyPersonalChat(personalRoom: string) {
    var room = this.state.rooms?.find((rm) => rm.group === personalRoom);

    if (room !== undefined) room.readOnly = true;

    this.state = { readOnly: true };
  }

  closePersonalChat(personalRoom: string) {
    if (this.state.rooms?.map((rm) => rm.group).includes(personalRoom)) {
      if (this.prePersonalChatroom !== undefined) {
        this.state = { activeRoom: this.prePersonalChatroom };
      }

      this.state = {
        rooms: this.state.rooms?.filter((rm) => rm.group !== personalRoom),
        messages: this.state.messages?.filter(
          (msg) => msg.group !== personalRoom
        ),
      };

      this.authSvc.userId$
        .pipe(
          switchMap((myuserid) => {
            return this.chatUserName$(myuserid as number).pipe(
              map((uname) => ({ myuserid, uname }))
            );
          })
        )
        .subscribe((a) => {
          this.signalrSend(
            new PersonalChatCloseRequest({
              userId: +personalRoom.substring(1),
              requestingUserId: a.myuserid!,
            })
          );
          setTimeout(
            () =>
              this.send(
                'Your chat with ' + a.uname + ' has ended.',
                personalRoom,
                true
              ),
            1000
          );
        });
    }
  }

  confirm(title: string, body: string) {
    const modalRef = this.modalSvc.open(ConfirmationModalComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = body;

    return modalRef.result;
  }

  getChatSupervisors() {
    this.http
      .get<ListChatSupervisorsVm>(`${this.chatApi}/chatsupervisors`)
      .pipe(map((vm) => (this.state = { chatSupervisors: vm.userDetails })))
      .subscribe();
  }

  isChatSupervisor(userId: number) {
    var returnVal = this.state.chatSupervisors
      ? this.state.chatSupervisors.some((u) => u.userId === userId)
      : false;

    return returnVal;
  }

  loadChatDetails() {
    if (!this.refreshSub) {
      this.refreshSub = merge(
        interval(5 * 60 * 1000), //5 mins
        this.refreshImmediately.asObservable()
      )
        .pipe(
          startWith(-1),
          switchMap(() => this.getRooms$()),
          switchMap((rooms) => {
            const existingRooms = this.state.rooms;
            const newRooms = rooms.filter(
              (r) =>
                !existingRooms ||
                !existingRooms.some((er) => er.group == r.group)
            );
            if (newRooms.length > 0) {
              this.addToGroups(newRooms.map((r) => r.group));
              return this.getMessages$(newRooms.map((r) => r.group)).pipe(
                map((messages) => ({ rooms, messages }))
              );
            } else {
              return of({ rooms, messages: [] });
            }
          })
        )
        .subscribe(({ rooms, messages }) => {
          const prerooms = this.state.rooms!;
          if (prerooms) {
            const privaterooms = prerooms.filter((r) => r.private === true);

            rooms = [...privaterooms, ...rooms];
          }

          const activeRoom =
            this.state.activeRoom &&
            (rooms?.some((r) => r.group === this.state.activeRoom) ||
              this.state.activeRoom === 'All Chat')
              ? this.state.activeRoom
              : rooms?.length > 0
              ? rooms
                  .map((r) => r.group)
                  .sort((a, b) => {
                    if (!isNaN(+a) && isNaN(+b)) return -1;
                    if (isNaN(+a) && !isNaN(+b)) return 1;
                    if (!isNaN(+a) && !isNaN(+b)) return +a - +b;
                    return collator.compare(a, b);
                  })[0]
              : undefined;

          /*for (const rm of rooms) {
            console.log('Object:', rm); // Logs the entire object
            console.log(
              'Name:',
              rm.group,
              'Group name:',
              rm.groupName,
              'Private:',
              rm.private
            ); // Logs specific properties
          }*/

          this.state = {
            rooms: rooms,
            messages: [
              ...(this.state.messages || []),
              ...messages.map((msg) => this.parseMessage(msg)),
            ],
            activeRoom: activeRoom,
          };
        });
    } else {
      this.refreshRooms();
    }
  }

  openUserList() {
    this.authSvc.userId$.pipe(take(1)).subscribe((uid) => {
      const modalRef = this.modalSvc.open(ChatUserListComponent, {
        size: 'sm',
        backdrop: true,
        keyboard: true,
      });

      modalRef.componentInstance.currentUserId = uid;
    });
  }

  pinMessage(messageId: number) {
    this.http.get(`${this.chatApi}/${messageId}/pin`).subscribe();
  }

  refreshRooms() {
    this.refreshImmediately.next();
  }

  requestChatroomUsers(chatroom: string) {
    this.signalrSend(new GetChatroomUsersRequest({ chatroom: chatroom }));
  }

  selectRoom(group: string) {
    var readonly = undefined;

    if (group !== 'All Chat')
      readonly = this.state.rooms?.filter((room) => room.group === group)[0]
        .readOnly;

    this.state = { activeRoom: group, readOnly: readonly };
  }

  send(msg: string, group: string, showSettings: boolean): void {
    // showSettings denotes empathy - so admin user.
    this.signalrSend(new MessageRequest({ group: group, message: msg }));
  }

  setAllChatRoom(room: string) {
    this.state = { allChatSelectedRoom: room };
  }

  showUserList(newstate: boolean) {
    this.state = { showUserList: newstate };
  }

  toggleAllChat(chatroom: string) {
    this.http
      .put(`${this.chatApi}/allchat`, { userId: -1, chatRoom: chatroom })
      .subscribe(() => this.refreshRooms());
  }

  toggleFavourite(chatroom: string) {
    this.http
      .put(`${this.chatApi}/favourite`, { userId: -1, chatRoom: chatroom })
      .subscribe(() => this.refreshRooms());
  }

  unpinMessage(messageId: number) {
    this.http.get(`${this.chatApi}/${messageId}/unpin`).subscribe();
  }
}

interface State {
  activeRoom?: string;
  allChatSelectedRoom: string;
  chatSupervisors?: ListChatSupervisorsChildDto[];
  chatroomUsers: Map<string, ChatroomUsers[]>;
  emojis?: EmojiDto[];
  hubState: HubConnectionState;
  messages?: ChatMessage[];
  readOnly: boolean;
  rooms?: ChatRoom[];
  showUserList: boolean;
}

const initalState: State = {
  activeRoom: undefined,
  allChatSelectedRoom: '',
  chatSupervisors: undefined,
  chatroomUsers: new Map<string, ChatroomUsers[]>(),
  hubState: HubConnectionState.Disconnected,
  messages: undefined,
  rooms: undefined,
  emojis: undefined,
  readOnly: false,
  showUserList: false,
};
